import { Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook"
import { selectCampaignProgressStats, getRunningCampaigns, selectActiveCampaignId } from "@common/reducers/CampaignSelectors"
import { getCampaignMessagesAndLogs, getCampaignRealStatus } from "@common/reducers/CampaignThunks"
import { computeCampaignProgress, CampaignProgressStats } from "@common/services/campaign-progress"
import { CAMPAIGN_STATUS_COLORS } from "@common/services/campaign-status-colors"
import { useNavigateCampaigns } from "@common-views/campaigns-redesign/open-campaign-page"
import { taskActions } from "@common/reducers/TasksReducer"
import { campaignSelectors } from "@common-reducers/DBServiceThunks"
import { useEffect } from "react"
import { cn } from "@/lib/utils"
import { RealCampaignStatus } from "./campaign-dialog-row"
import { useTranslation } from 'react-i18next'
import { namespace } from '@common-config/constants'

type Props = {
    campaignId?: string
    className?: string
    onClick?: () => void
    compact?: boolean  // Force compact vertical layout for narrow sidebars
}

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const MultiColorProgressBadge = ({ stats, status, totalContacts, t, compact }: { stats: CampaignProgressStats; status: string; totalContacts: number; t: any; compact?: boolean }) => {
    // Single source of truth: the bar's filled segments and the "X/Y" counter both come from here,
    // so they can never disagree. A message dispatched to WhatsApp counts as sent/processed; only
    // still-queued (pending) messages stay the unfilled remainder.
    const { total, totalProcessed, totalFailed, isAllProcessed, percents } = computeCampaignProgress(stats, totalContacts);

    if (total === 0) return null;

    const {
        sending: sendingPercent,
        delivered: deliveredPercent,
        read: readPercent,
        interacted: interactedPercent,
        failed: failedPercent,
        expired: expiredPercent,
    } = percents;
    const expiredCount = stats.expired || 0;

    if (status === 'completed' || isAllProcessed) {
        return (
            <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg font-medium">
                    <CheckCircle2 className="w-4 h-4" />
                    {t('campaignStatus.completed')}
                </div>
                {totalFailed > 0 && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="flex items-center gap-1 px-2 py-2 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 rounded-lg border border-rose-200 dark:border-rose-800">
                                <AlertCircle className="w-4 h-4" />
                                <span className="text-sm font-medium">{totalFailed}</span>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{t('campaignProgress.failed', { count: totalFailed })}</p>
                        </TooltipContent>
                    </Tooltip>
                )}
            </div>
        );
    }

    const renderSegment = (percent: number, color: string, label: string) => {
        if (percent <= 0) return null;
        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className="h-full transition-all duration-500"
                        style={{ width: `${percent}%`, background: color }}
                    />
                </TooltipTrigger>
                <TooltipContent>
                    <p>{label}</p>
                </TooltipContent>
            </Tooltip>
        );
    };

    return (
        <div className={`flex ${compact ? 'flex-col gap-1.5 px-3' : 'flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 px-3 sm:px-4'} py-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700`}>
            <div className="flex items-center gap-2">
                <Send className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-pulse flex-shrink-0" />
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                    {totalProcessed}/{total}
                </span>
            </div>
            {/* Colours from the single source of truth (CAMPAIGN_STATUS_COLORS) so the
                toolbar badge and the campaign page read as one system. */}
            <div className={`${compact ? 'w-full' : 'w-full sm:w-24'} h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden flex`}>
                {renderSegment(sendingPercent, CAMPAIGN_STATUS_COLORS.sending, t('campaignStatus.sending', { count: stats.sending }))}
                {renderSegment(deliveredPercent, CAMPAIGN_STATUS_COLORS.delivered, t('campaignStatus.delivered', { count: stats.delivered }))}
                {renderSegment(readPercent, CAMPAIGN_STATUS_COLORS.read, t('campaignStatus.read', { count: stats.read }))}
                {renderSegment(interactedPercent, CAMPAIGN_STATUS_COLORS.interacted, t('campaignStatus.interacted', { count: stats.interacted }))}
                {renderSegment(failedPercent, CAMPAIGN_STATUS_COLORS.failed, t('campaignStatus.failed', { count: stats.failed }))}
                {renderSegment(expiredPercent, CAMPAIGN_STATUS_COLORS.failed, t('campaignProgress.expired', { count: expiredCount }))}
            </div>
        </div>
    );
};

export const InProgressCampaignIndicator = ({
    campaignId,
    className,
    onClick,
    compact = false
}: Props) => {
    const dispatch = useAppDispatch()
    const navigateCampaigns = useNavigateCampaigns()
    const { t } = useTranslation(namespace)

    // Get running campaigns to find the latest one if campaignId is not provided
    const runningCampaigns = useAppSelector(state => getRunningCampaigns(state))
    
    // Determine which campaign to use
    // Determine which campaign to use
    const activeCampaignId = useAppSelector(state => selectActiveCampaignId(state, campaignId));

    const campaign = useAppSelector(state => activeCampaignId ? campaignSelectors.selectById(state, activeCampaignId) : null)
    const progressStats = useAppSelector(state => activeCampaignId ? selectCampaignProgressStats(state, activeCampaignId) : null)
    const realStatus = campaign ? (getCampaignRealStatus(campaign?.campaignStatus, !!campaign?.contactCount, (campaign as any)?.updatedAt ?? campaign?.schedule?.dueDate) as RealCampaignStatus) : null

    useEffect(() => {
        if (activeCampaignId) {
            dispatch(getCampaignMessagesAndLogs(activeCampaignId))
        }
    }, [activeCampaignId, dispatch])

    if (!campaign || !progressStats) return null;

    const status = realStatus === 'Completed' ? 'completed' : 'sending';

    // Get total contact count from campaign
    const totalContacts = campaign?.contactCount || campaign?.list?.contactIdList?.length || 0;

    // The campaigns overlay is ROUTE-driven: the legacy campaignLogsView flags
    // this used to set are not read by it, so the overlay opened on a stale route.
    const handleOpenLogs = () => {
        if (activeCampaignId) {
            dispatch(taskActions.setIsTasksPanelOpen(false));
            navigateCampaigns(`/campaigns/${activeCampaignId}/logs`);
        }
    };

    return (
        <div
            key={activeCampaignId}
            onClick={onClick || handleOpenLogs}
            className={cn(
                "flex flex-col items-end gap-1 cursor-pointer",
                className
            )}
        >
            <MultiColorProgressBadge
                stats={progressStats}
                status={status}
                totalContacts={totalContacts}
                t={t}
                compact={compact}
            />
        </div>
    )
}

export default InProgressCampaignIndicator